/**
 * ModelRegistry — fonte ÚNICA de metadados dos modelos GLB (pipeline da fase de modelos).
 *
 * Guarda, por entidade: assetPath, scale, rotationY, yOffset, healthBarHeight, shadowRadius,
 * mapa de animação (estado lógico → candidatos de nome de clip) e config de sockets de
 * equipamento. NÃO define alcance/hitbox/hook range/raio de gameplay — isso permanece no
 * GameConfig/EnemyDef (§11 Collider ≠ Visual). Enquanto não houver GLB no path, o
 * ModelLoader falha graciosamente e a entidade cai no procedural atual (§12 Fallback).
 *
 * Os paths e budgets seguem 02_ASSET_MANIFEST.json / 04_IMPORT_SPEC.md do Model Integration
 * Pack. As fontes recomendadas (Quaternius/Kenney CC0) exigem download + verificação de
 * licença comercial ANTES de commitar o asset — este arquivo só declara ONDE cada GLB entra.
 */

/** Ponto de fixação de equipamento no rig (§4). */
export type SocketName = "RightHand" | "LeftHand" | "Back" | "Head" | "Chest";

/** Estado de animação LÓGICO (mapeado para nomes reais de clip no GLB via animationMap). */
export type AnimState =
  // player
  | "Idle" | "Walk" | "Run" | "Sprint"
  | "SwordAttack" | "AxeAttack" | "SpearAttack" | "BowDraw" | "BowRelease" | "HookThrow"
  | "Hit" | "Death"
  // inimigo (comum)
  | "Move" | "Attack"
  // voadores
  | "IdleFly" | "Fly" | "AttackFly" | "HitFly" | "Grounded"
  // opcionais/elite
  | "Charge" | "Special1" | "Special2" | "Stagger" | "Hooked";

/** Candidatos de nome de clip por estado. Casados por substring, case-insensitive, na ordem. */
export type AnimationMap = Partial<Record<AnimState, string[]>>;

/** Ajuste de fixação (posição/rotação/escala) aplicado a um objeto preso a um socket. */
export interface AttachOffset {
  position?: [number, number, number];
  rotationEuler?: [number, number, number];
  scale?: number;
}

/** Config de um socket: bones candidatos (substring/case-insensitive) + ajuste opcional. */
export interface SocketConfig extends AttachOffset {
  boneNames: string[];
}

/** Offset da arma na mão por visualId (§19). Alinha a mesh (blade em +Y) ao bone da mão.
 * Valores placeholder até o Hunter final chegar (ajuste fino no registry, sem tocar gameplay). */
export const WEAPON_SOCKET_OFFSETS: Readonly<Record<string, AttachOffset>> = {
  sword: { rotationEuler: [Math.PI / 2, 0, 0], scale: 1 },
  shardSaber: { rotationEuler: [Math.PI / 2, 0, 0], scale: 1 },
  galeBlade: { rotationEuler: [Math.PI / 2, 0, 0], scale: 1 },
  axe: { rotationEuler: [Math.PI / 2, 0, 0.2], scale: 1 },
  tempestCleaver: { rotationEuler: [Math.PI / 2, 0, 0.2], scale: 1 },
  spear: { rotationEuler: [Math.PI / 2, 0, 0], scale: 1 },
  cragSpear: { rotationEuler: [Math.PI / 2, 0, 0], scale: 1 },
  thunderpike: { rotationEuler: [Math.PI / 2, 0, 0], scale: 1 },
  bow: { rotationEuler: [0, 0, Math.PI / 2], scale: 1 },
  arcLongbow: { rotationEuler: [0, 0, Math.PI / 2], scale: 1 },
};

/** Offset de fixação da arma (ou {} se não houver override específico). */
export function weaponSocketOffset(visualId: string): AttachOffset {
  return WEAPON_SOCKET_OFFSETS[visualId] ?? {};
}

/** Override de material p/ dar identidade VOID a uma base CC0 sem editar o GLB (§35). */
export interface MaterialOverride {
  /** cor-base (mesclada por `tintAmount`). */
  tintColor?: number;
  /** 0..1 quanto puxar a cor original para `tintColor` (default 0.35). */
  tintAmount?: number;
  /** brilho de energia (emissive) da identidade VOID. */
  emissive?: number;
  emissiveIntensity?: number;
  roughness?: number;
  metalness?: number;
}

export interface ModelEntry {
  readonly key: string;
  /** Path do GLB relativo à base do app (servido de `public/`). Offline no APK (§14). */
  readonly assetPath: string;
  /** Tentar carregar no preload? (false = documentado mas não buscado). */
  readonly enabled: boolean;
  readonly kind: "player" | "enemy";
  readonly scale: number;
  readonly rotationY: number;
  readonly yOffset: number;
  /** Altura da barra de vida (mundo, antes do scale). Ausente = usa o valor de gameplay. */
  readonly healthBarHeight?: number;
  readonly shadowRadius?: number;
  readonly animationMap: AnimationMap;
  readonly sockets?: Partial<Record<SocketName, SocketConfig>>;
  /** GLBs extras só de animação, mesclados ao modelo (Quaternius: base + Animation Library
   * compartilham o mesmo esqueleto). Ausentes = ignorados. */
  readonly animationSources?: string[];
  /** Identidade VOID aplicada por override sobre a base CC0 (§35). */
  readonly materialOverrides?: MaterialOverride;
  /** Faixa de triângulos-alvo [min,max] p/ relatório/validação mobile (§32). */
  readonly triBudget?: readonly [number, number];
}

// ---- mapas de animação reutilizáveis (candidatos generosos p/ casar clips reais) ----

// Mapa do Hunter: nomes EXATOS dos clips do pack (UAL1) primeiro, depois genéricos (fallback
// p/ um futuro hunter.glb diferente). Ordem importa — evita "Attack" casar Sword p/ Axe/Spear.
const PLAYER_ANIM: AnimationMap = {
  Idle: ["Idle_Loop", "Idle", "Idle01", "Standing", "Breathing"],
  Walk: ["Walk_Loop", "Walk", "Walking"],
  Run: ["Jog_Fwd_Loop", "Jog", "Run", "Running"],
  Sprint: ["Sprint_Loop", "Sprint", "FastRun", "Run"],
  SwordAttack: ["Sword_Attack", "Sword", "Slash", "Swing", "1H_Melee", "Melee"],
  AxeAttack: ["Punch_Cross", "Cross", "Axe", "Chop", "HeavyAttack", "Smash", "2H_Melee"],
  SpearAttack: ["Punch_Jab", "Jab", "Spear", "Stab", "Thrust"],
  // sem clip de arco na library → usa o "loose" do Spell_Simple (NÃO pistola, §7).
  BowDraw: ["Spell_Simple_Shoot", "Spell", "Bow_Draw", "Draw", "Aim", "Bow"],
  BowRelease: ["Spell_Simple_Shoot", "Shoot", "Release", "Fire"],
  HookThrow: ["Spell_Simple_Shoot", "Spell", "Throw", "Cast", "Interact", "Hook"],
  Hit: ["Hit_Chest", "Hit", "Damage", "Impact", "React", "Flinch"],
  Death: ["Death01", "Death", "Die", "Dead"],
};

const ENEMY_GROUND_ANIM: AnimationMap = {
  Idle: ["Idle", "Idle_A", "IdleNormal"],
  Move: ["Run", "Walk", "Walking", "Running", "Move"],
  Attack: ["Attack", "Bite", "Melee", "Attack_A", "Headbutt", "Claw"],
  Charge: ["Charge", "Run_Fast", "Sprint", "Dash", "Gallop"],
  Hit: ["Hit", "Hit_A", "Damage", "Impact", "React", "Flinch", "TakeDamage"],
  Stagger: ["Stagger", "Stunned", "KnockedDown", "Hit"],
  Hooked: ["Stagger", "Pulled", "Grab", "Hit"],
  Death: ["Death", "Death_A", "Die", "Dead"],
  Special1: ["Special", "Special1", "Roar", "Cast"],
  Special2: ["Special2", "Cast", "Roar"],
};

const ENEMY_FLYER_ANIM: AnimationMap = {
  Idle: ["Fly_Idle", "IdleFly", "Hover", "Idle"],
  IdleFly: ["Fly_Idle", "IdleFly", "Hover", "Idle"],
  Move: ["Fly", "Flying", "Flap", "Fly_Forward"],
  Fly: ["Fly", "Flying", "Flap", "Fly_Forward"],
  Attack: ["Fly_Attack", "AttackFly", "Attack", "Dive", "Peck"],
  AttackFly: ["Fly_Attack", "AttackFly", "Attack", "Dive"],
  Hit: ["Fly_Hit", "HitFly", "Hit", "Damage"],
  HitFly: ["Fly_Hit", "HitFly", "Hit"],
  Grounded: ["Grounded", "Downed", "Land", "Death"],
  Hooked: ["Grounded", "Downed", "Stagger", "Hit"],
  Death: ["Death", "Die", "Dead"],
};

// ---- sockets padrão do rig humanoide (Player + Tempest Warden) ----

const HUMANOID_SOCKETS: Partial<Record<SocketName, SocketConfig>> = {
  RightHand: { boneNames: ["RightHand", "Hand_R", "hand.R", "mixamorigRightHand", "Wrist_R"] },
  LeftHand: { boneNames: ["LeftHand", "Hand_L", "hand.L", "mixamorigLeftHand", "Wrist_L"] },
  Back: { boneNames: ["Spine2", "Spine_02", "Chest", "spine.003", "mixamorigSpine2"], position: [0, 0.05, -0.18] },
  Head: { boneNames: ["Head", "head", "mixamorigHead"] },
  Chest: { boneNames: ["Spine1", "Spine_01", "Chest", "spine.002", "mixamorigSpine1"], position: [0, 0, 0.12] },
};

/** Budgets de tri por classe (02_ASSET_MANIFEST.json) — usados só para relatório/validação. */
export const TRIANGLE_BUDGETS = {
  player: [8000, 20000] as const,
  normal: [2000, 8000] as const,
  elite: [6000, 15000] as const,
  boss: [12000, 30000] as const,
};

/**
 * Tabela mestra. `enabled: true` = o preload tenta buscar o GLB (e cai no procedural se
 * faltar). Todos começam SEM asset presente (o Model Pack não redistribui modelos), então
 * hoje todos resolvem para o fallback procedural — nenhuma entidade está "migrada".
 */
export const MODEL_REGISTRY: Readonly<Record<string, ModelEntry>> = {
  // ---------- PLAYER ----------
  player_hunter: {
    key: "player_hunter",
    assetPath: "models/player/hunter.glb",
    // DESLIGADO por decisão do Thiago (10.4C): o Hunter PROCEDURAL (encapuzado) ficou melhor
    // que a base CC0 "superhero". GLBs movidos p/ assets/models/samples/ (fora do APK). Para
    // reativar o GLB: enabled:true + copiar os .glb de volta p/ public/models/player/.
    enabled: false,
    kind: "player",
    // hunter.glb = Quaternius Universal Base Character (Superhero_Male, CC0), ~1.83m nativo.
    // rotationY = PI: a base encara -Z; girar 180° faz a frente = +Z (direção de movimento).
    scale: 1.0,
    rotationY: Math.PI,
    yOffset: 0,
    healthBarHeight: 2.2,
    shadowRadius: 0.9,
    animationMap: PLAYER_ANIM,
    sockets: HUMANOID_SOCKETS,
    // Fluxo Quaternius: personagem-base (hunter.glb) + biblioteca de animação separada
    // (mesmo esqueleto). Se o hunter.glb já trouxer clips embutidos, deixe este arquivo
    // ausente — o merge só acontece se hunter_anims.glb existir.
    animationSources: ["models/player/hunter_anims.glb"],
    // identidade VOID sobre a base CC0 (§21/§35): corpo carvão-violeta escuro (não roxo
    // uniforme) + brilho de energia sutil. Cristais/visor vêm por attachment (HunterIdentity).
    materialOverrides: { tintColor: 0x2f2b47, tintAmount: 0.5, emissive: 0x4322a0, emissiveIntensity: 0.15 },
    triBudget: [8000, 20000],
  },

  // ---------- REGIÃO 1 (foco desta fase) ----------
  voidling: { key: "voidling", assetPath: "models/enemies/shardling.glb", enabled: true, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM, triBudget: [2000, 6000] },
  voidRunner: { key: "voidRunner", assetPath: "models/enemies/riftclaw.glb", enabled: true, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM, triBudget: [3000, 8000] },
  voidBrute: { key: "voidBrute", assetPath: "models/enemies/stoneback.glb", enabled: true, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM, triBudget: [5000, 12000] },
  // Elite variante do brute — reaproveita o GLB do Stoneback (tratamento de elite no Enemy).
  corruptedBrute: { key: "corruptedBrute", assetPath: "models/enemies/stoneback.glb", enabled: true, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM, triBudget: [5000, 12000] },

  // ---------- REGIÃO 2 (documentado; migração posterior) ----------
  shardfang: { key: "shardfang", assetPath: "models/enemies/shardfang.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM },
  craghorn: { key: "craghorn", assetPath: "models/enemies/craghorn.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM },
  aetherwing: { key: "aetherwing", assetPath: "models/enemies/aetherwing.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_FLYER_ANIM },
  glintback: { key: "glintback", assetPath: "models/enemies/glintback.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM },
  shardWarden: { key: "shardWarden", assetPath: "models/enemies/shard_warden.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM, sockets: HUMANOID_SOCKETS },

  // ---------- REGIÃO 3 (documentado; migração posterior) ----------
  galestrider: { key: "galestrider", assetPath: "models/enemies/galestrider.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM },
  arcbeak: { key: "arcbeak", assetPath: "models/enemies/arcbeak.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_FLYER_ANIM },
  stormback: { key: "stormback", assetPath: "models/enemies/stormback.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM },
  cloudstalker: { key: "cloudstalker", assetPath: "models/enemies/cloudstalker.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM },
  tempestWarden: { key: "tempestWarden", assetPath: "models/enemies/tempest_warden.glb", enabled: false, kind: "enemy", scale: 1, rotationY: 0, yOffset: 0, animationMap: ENEMY_GROUND_ANIM, sockets: HUMANOID_SOCKETS },
};

/** Entrada por chave direta (player_hunter ou def.id do inimigo). */
export function modelEntry(key: string): ModelEntry | null {
  return MODEL_REGISTRY[key] ?? null;
}
